import { logger } from 'module/logger';
import { HttpClient } from 'api/http-client';
import { GroupedTimelines } from 'api/timeline/types';
import {
  SignalSummaryRequest,
  SignalSummaryResponse,
  SignalSummaryValue,
  SignalSummaryValueApi,
  DetailedSummaryItem,
  ReasonsRequest,
  ReasonsResponse,
  TimelineRequest,
} from './types';

// Hard-coded mock response for reasons
const generateMockReasonsResponse = (): ReasonsResponse => {
  return {
    '11': {
      '17': [
        'Project creation failed to trigegr, causing inconsistent project setup',
        'Email notification failed to fire despite valid data being present',
      ],
      '18': [
        'EWS logs displayed anomalies such as `on-hold` request that shouldnt appear',
      ],
    },
    '13': {
      '39': [
        'Interest was expressed in using Rocketlane training material as part of new employee onboarding',
      ],
    },
  };
};

// Transform API response to internal format
// Note: Period grouping is now done in index.tsx based on requested period, not firstSignalOccurrenceTime
const transformSummaryResponse = (
  apiResponse: SignalSummaryResponse,
): { groupBy: string; values: SignalSummaryValue[] } => {
  const groupBy = apiResponse.groupBy || 'MONTH';

  // Transform each API value entry to internal format
  // All entries are returned as-is, grouping by requested period happens in index.tsx
  const transformedValues: SignalSummaryValue[] = apiResponse.values.map(
    (apiValue: SignalSummaryValueApi) => {
      const value: SignalSummaryValue = {};

      if (apiValue.RISK) {
        value.Risk = {
          ...apiValue.RISK,
          detailedSummary: transformDetailedSummary(
            apiValue.RISK.detailedSummary,
          ),
        };
      }

      if (apiValue.OPERATIONAL) {
        value.Operational = {
          ...apiValue.OPERATIONAL,
          detailedSummary: transformDetailedSummary(
            apiValue.OPERATIONAL.detailedSummary,
          ),
        };
      }

      if (apiValue.OPPORTUNITY) {
        value.Opportunity = {
          ...apiValue.OPPORTUNITY,
          detailedSummary: transformDetailedSummary(
            apiValue.OPPORTUNITY.detailedSummary,
          ),
        };
      }

      return value;
    },
  );

  return {
    groupBy,
    values: transformedValues,
  };
};

// Transform detailedSummary from object to array format
const transformDetailedSummary = (
  detailedSummary: DetailedSummaryItem[] | { [key: string]: string },
): DetailedSummaryItem[] => {
  // If it's already an array, return as is
  if (Array.isArray(detailedSummary)) {
    return detailedSummary;
  }

  // If it's an object, convert to array format
  if (typeof detailedSummary === 'object' && detailedSummary !== null) {
    return Object.entries(detailedSummary).map(([title, content]) => ({
      title: title.startsWith('*') ? title.substring(1).trim() : title.trim(),
      content: typeof content === 'string' ? content : String(content),
    }));
  }

  // Fallback to empty array
  return [];
};

class SignalOccurrencesAPIClient extends HttpClient {
  public getSummary = async (
    companyId: number,
    payload: SignalSummaryRequest,
  ): Promise<{ groupBy: string; values: SignalSummaryValue[] }> => {
    const apiResponse = await this.post<
      SignalSummaryResponse,
      SignalSummaryRequest
    >(`/signal-occurrences/${companyId}/summary`, payload);

    return transformSummaryResponse(apiResponse);
  };

  public getReasons = async (
    companyId: number,
    payload: ReasonsRequest,
  ): Promise<ReasonsResponse> => {
    try {
      return await this.post<ReasonsResponse, ReasonsRequest>(
        `/signal-occurrences/${companyId}/reasons`,
        payload,
      );
    } catch (error) {
      logger.error('Error getting signal reasons', { error });

      // Return hard-coded mock response
      return generateMockReasonsResponse();
    }
  };

  public getTimeline = async (
    companyId: number,
    payload: TimelineRequest,
  ): Promise<GroupedTimelines> => {
    return await this.post<GroupedTimelines, TimelineRequest>(
      `/signal-occurrences/${companyId}/timeline`,
      payload,
    );
  };
}

export const SignalOccurrencesAPI = new SignalOccurrencesAPIClient();
